import React, { useState, useEffect } from 'react';
import { Archive, Shield, Calendar, Download, CheckCircle } from 'lucide-react';

const ComplianceArchive = ({ onSubmit, onPrevious, initialData = {} }) => {
  const [retentionPolicy, setRetentionPolicy] = useState(initialData.retentionPolicy || '7years');
  const [wormStorage, setWormStorage] = useState(initialData.wormStorage || false);
  const [archiveCreated, setArchiveCreated] = useState(initialData.archiveCreated || false);
  const [archiveInfo, setArchiveInfo] = useState(initialData.archiveInfo || null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    // Load initial data when component mounts
  }, [initialData]);

  const createArchive = async () => {
    setIsCreating(true);
    
    // Simulate archive creation
    await new Promise(resolve => setTimeout(resolve, 5000));
    
    const archiveData = {
      filename: 'compliance_archive_v1.zip',
      size: '15.2 MB',
      checksum: 'sha256:a1b2c3d4e5f6...',
      createdAt: new Date().toISOString(),
      retentionUntil: new Date(Date.now() + 7 * 365 * 24 * 60 * 60 * 1000).toISOString(),
      contents: [
        'report_v1_final.pdf',
        'auditor_opinion.pdf',
        'signoff_protocol.pdf',
        'methodology.pdf',
        'assumptions_log.json',
        'factors_gwp.json',
        'data_log_summary.csv',
        'evidence_manifest.json',
        'checksums.txt'
      ],
      settings: {
        retentionPolicy,
        wormStorage
      }
    };
    
    setArchiveInfo(archiveData);
    setArchiveCreated(true);
    setIsCreating(false);
  };


  const getRetentionDate = () => {
    const years = parseInt(retentionPolicy);
    const date = new Date();
    date.setFullYear(date.getFullYear() + years);
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Compliance Archive</h2>
        <p className="text-gray-600">Create immutable archive for regulatory compliance</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Configuration */}
        <div className="space-y-6">
          {/* Archive Contents */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Archive Contents (ZIP)</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                report_vX_final.pdf
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                auditor_opinion.pdf
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                signoff_protocol.pdf
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                methodology.pdf / .md
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                assumptions_log.json (Approved only)
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                factors_gwp.json (pinned sources & GWP)
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                data_log_summary.csv (no raw PII)
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                evidence_manifest.json (hashes from Step 9 bundles)
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                checksums.txt (SHA-256 for each file)
              </div>
            </div>
          </div>

          {/* Retention Policy */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Retention Policy</h3>
            <select
              value={retentionPolicy}
              onChange={(e) => setRetentionPolicy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="3years">3 years</option>
              <option value="5years">5 years</option>
              <option value="7years">7 years (recommended)</option>
              <option value="10years">10 years</option>
              <option value="permanent">Permanent</option>
            </select>
            {retentionPolicy !== 'permanent' && (
              <p className="text-sm text-gray-500 mt-1">
                Archive will be retained until: {getRetentionDate()}
              </p>
            )}
          </div>

          {/* Storage Options */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Storage</h3>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={wormStorage}
                onChange={(e) => setWormStorage(e.target.checked)}
                className="mr-2"
              />
              <Shield className="w-4 h-4 mr-2 text-gray-500" />
              <span className="text-sm text-gray-700">"Write-once" flag (WORM) if available</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              WORM storage prevents modification or deletion of archived files
            </p>
          </div>

          {/* Archive Features */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Archive Features</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center">
                <Shield className="w-4 h-4 text-blue-500 mr-2" />
                Immutable storage (cannot be modified)
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                SHA-256 checksums for integrity verification
              </div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 text-orange-500 mr-2" />
                Automatic retention management
              </div>
              <div className="flex items-center">
                <Archive className="w-4 h-4 text-purple-500 mr-2" />
                Regulatory compliance ready
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Creation & Status */}
        <div className="space-y-6">
          {/* Create Archive Button */}
          <div className="text-center">
            <button
              onClick={createArchive}
              disabled={isCreating}
              className={`flex items-center justify-center w-full px-6 py-4 rounded-md transition-colors ${
                isCreating
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              <Archive className="w-5 h-5 mr-2" />
              {isCreating ? 'Creating Archive...' : 'Create Compliance Archive'}
            </button>
          </div>

          {/* Archive Info */}
          {archiveCreated && archiveInfo && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center mb-3">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                <h3 className="font-semibold text-green-800">Archive Created Successfully</h3>
              </div>
              <div className="space-y-2 text-sm text-green-700">
                <p><strong>Filename:</strong> {archiveInfo.filename}</p>
                <p><strong>Size:</strong> {archiveInfo.size}</p>
                <p><strong>Checksum:</strong> {archiveInfo.checksum}</p>
                <p><strong>Created:</strong> {new Date(archiveInfo.createdAt).toLocaleString()}</p>
                <p><strong>Retention until:</strong> {new Date(archiveInfo.retentionUntil).toLocaleDateString()}</p>
              </div>
              <button className="mt-3 flex items-center text-green-600 hover:text-green-700">
                <Download className="w-4 h-4 mr-1" />
                Download Archive
              </button>
            </div>
          )}

          {/* Archive Contents List */}
          {archiveCreated && archiveInfo && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-3">Archive Contents</h3>
              <div className="space-y-1 text-sm text-blue-700 max-h-48 overflow-y-auto">
                {archiveInfo.contents.map((file, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="w-3 h-3 text-green-500 mr-2" />
                    {file}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Settings Summary */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-3">Archive Configuration</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <p><strong>Retention Policy:</strong> {retentionPolicy.replace('years', ' years')}</p>
              <p><strong>WORM Storage:</strong> {wormStorage ? 'Enabled' : 'Disabled'}</p>
              <p><strong>Files Included:</strong> {archiveInfo?.contents?.length || 0}</p>
              <p><strong>Archive Status:</strong> {archiveCreated ? 'Created' : 'Pending'}</p>
            </div>
          </div>

          {/* Compliance Notes */}
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="font-semibold text-yellow-800 mb-2">Compliance Notes</h3>
            <div className="space-y-1 text-sm text-yellow-700">
              <p>• Archive is immutable and cannot be modified</p>
              <p>• All files include SHA-256 checksums for integrity</p>
              <p>• Retention policy ensures regulatory compliance</p>
              <p>• Archive can be used for audit purposes</p>
            </div>
          </div>
        </div>
      </div>


      {/* Completion Message */}
      {archiveCreated && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
            <h3 className="font-semibold text-green-800">Publish Workflow Complete!</h3>
          </div>
          <p className="text-sm text-green-700 mt-2">
            Your sustainability report has been successfully published and archived. 
            All compliance requirements have been met.
          </p>
        </div>
      )}
    </div>
  );
};

export default ComplianceArchive;
